import { useMutation, useQuery } from "@apollo/client"
import { dropTargetForElements, monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter"
import { extractClosestEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge"
import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import invariant from "tiny-invariant"
import ExampleItem from "./example_item"
import { cacheListRemove } from "@/admin/util/graphql"
import { graphql } from "@/api"
import { type SignTag, type SignTagExamplesQuery } from "@/api/graphql"
import { setEnabledFlags } from "@/signomatic/app/flags"
import { ArrowDown, ArrowUp } from "@/skylticons"

const signtagDoc = graphql(`
  query SignTagExamples($id: ID!, $vatCode: String) {
    signTag(id: $id) {
      id

      examples {
        id
        sortOrder

        ...SignTagExample_Example
      }
    }
  }
`)

const exampleSignsDeleteDoc = graphql(`
  mutation ExampleSignProductPagesDelete($ids: [ID!]!) {
    examplesDelete(ids: $ids) {
      ids
    }
  }
`)

const reorderDoc = graphql(`
  mutation ExamplesSortOrderUpdate($ids: [ID!]!) {
    examplesSortOrderUpdate(ids: $ids) {
      examples {
        id
        sortOrder
      }
    }
  }
`)

interface SignTagExamplesProps {
  signTagId: string
  locale: string
  signCategoryId: string
  admin: boolean
  adminLoggedIn: boolean
  vatCode: string
  perPage: number
  business: boolean
  featureFlags: string[]
}

const SignTagExamples: React.FC<SignTagExamplesProps> = ({
  signTagId,
  locale,
  signCategoryId,
  admin,
  adminLoggedIn,
  vatCode,
  perPage,
  business,
  featureFlags,
}) => {
  const { data } = useQuery(signtagDoc, {
    variables: { id: signTagId, vatCode },
  })
  const [disconnectMutation] = useMutation(exampleSignsDeleteDoc)
  const { t } = useTranslation()
  const sortedExamples = data?.signTag?.examples || []

  const [displayedCount, setDisplayedCount] = useState(perPage)
  const dragContainer = useRef<HTMLDivElement>(null)
  const [entered, setEntered] = useState(false)
  const [dragInstance] = useState(() => Symbol("drag-instance"))
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    setHydrated(true)
  }, [])

  setEnabledFlags(featureFlags)

  useEffect(() => {
    const el = dragContainer.current
    invariant(el)

    return dropTargetForElements({
      element: el,
      getData: () => ({ location }),
      onDragEnter: () => setEntered(true),
      onDragLeave: () => setEntered(false),
      onDrop: () => setEntered(false),
    })
  }, [entered])

  const [orderMutation] = useMutation(reorderDoc)
  const sortedExampleIds = sortedExamples.map(item => item.id)

  useEffect(() => {
    return monitorForElements({
      canMonitor({ source }) {
        return source.data.instanceId === dragInstance
      },
      onDrop({ location, source }) {
        if (!location.current.dropTargets.length || !location.initial.dropTargets.length) {
          return
        }

        const exampleId = source.data.exampleId
        invariant(typeof exampleId === "string")

        const targetExample = location.current.dropTargets[0]
        const closestEdgeOfTarget = extractClosestEdge(targetExample.data)
        const targetId = targetExample.data.exampleId

        invariant(typeof targetId === "string")

        if (targetId === exampleId) {
          return
        }

        const sortedIds = [...sortedExampleIds].filter(id => id !== exampleId)
        const targetIndex = sortedIds.indexOf(targetId)

        switch (closestEdgeOfTarget) {
          case "left":
            sortedIds.splice(targetIndex, 0, exampleId)
            break
          case "right":
            sortedIds.splice(targetIndex + 1, 0, exampleId)
            break
        }

        orderMutation({
          variables: { ids: sortedIds },
          optimisticResponse: {
            examplesSortOrderUpdate: {
              examples: sortedIds.map((id, index) => ({
                id,
                sortOrder: index,
              })),
            },
          },
          update: (cache, { data }) => {
            const examples = data?.examplesSortOrderUpdate?.examples
            if (!examples) return

            const existingData = cache.readQuery({
              query: signtagDoc,
              variables: { id: signTagId, vatCode },
            })

            const existingExamples = existingData?.signTag?.examples

            if (!existingExamples) return

            const order = existingExamples.slice().sort((a, b) => sortedIds.indexOf(a.id) - sortedIds.indexOf(b.id))

            cache.writeQuery<SignTagExamplesQuery>({
              query: signtagDoc,
              variables: { id: signTagId, vatCode },
              data: {
                signTag: {
                  id: signTagId,
                  examples: order,
                },
              },
            })
          },
        }).catch(error => console.error(error))
      },
    })
  }, [dragInstance, sortedExampleIds, orderMutation, signTagId, vatCode])

  const disconnectExampleItem = (id: string) => {
    const example = sortedExamples.find(example => example.id === id)
    if (!example) return

    if (confirm("Are you sure")) {
      disconnectMutation({
        variables: { ids: id },
        optimisticResponse: {
          examplesDelete: {
            ids: [id],
          },
        },
        update(cache, { data }) {
          const deletedIds = data?.examplesDelete?.ids
          if (!deletedIds) return

          cache.modify<SignTag>({
            id: cache.identify(example),
            fields: {
              examples: cacheListRemove("id", deletedIds),
            },
          })
        },
        refetchQueries: ["SignTagExamples"],
        onError: error => console.error(error),
      })
    }
  }

  const examplesToLoad = sortedExamples.length - displayedCount

  const showMoreExamples = () => {
    setDisplayedCount(prevCount => prevCount + (examplesToLoad > perPage ? perPage : examplesToLoad))
  }

  const hideExamples = () => {
    const exampleScrollTarget = document.querySelector<HTMLDivElement>('[data-examples-target="examplesScroll"]')
    setDisplayedCount(perPage)

    if (exampleScrollTarget) {
      exampleScrollTarget.scrollIntoView({ behavior: "smooth" })
    }
  }

  return (
    <div ref={dragContainer} className="examples-container">
      <div className="examples-row mb-8 grid auto-rows-max gap-3 gap-y-4 xs:grid-cols-2 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4">
        {sortedExamples.slice(0, locale === "en-GB" ? sortedExamples.length : displayedCount).map(example => (
          <ExampleItem
            key={example.id}
            signCategoryId={signCategoryId}
            admin={admin}
            adminLoggedIn={adminLoggedIn}
            example={example}
            disconnect={disconnectExampleItem}
            dragInstance={dragInstance}
            business={business}
          />
        ))}
      </div>
      {sortedExamples.length > perPage && locale !== "en-GB" && hydrated && (
        <div className="button-container">
          {displayedCount < sortedExamples.length && (
            <button
              onClick={showMoreExamples}
              className="button button-secondary button-medium icon-right display-examples mb-0"
              type="button"
              data-gtm="lp-more-examples-button"
            >
              {t("examples.more_examples")}
              <ArrowDown />
            </button>
          )}
          {examplesToLoad === 0 && (
            <button
              onClick={hideExamples}
              className="button button-secondary button-medium icon-right hide-examples mb-0"
              type="button"
            >
              {t("examples.less_examples")}
              <ArrowUp />
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default SignTagExamples
